import { Formik } from 'formik'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import InputFormik from '../../input-formik'
import Button from '../../tailwind/Button'
import Toggle from '../../tailwind/toggle'

// Helper function to sanitize filename
const sanitizeFileName = (filename) => {
  if (!filename) return ''
  // Replace spaces with underscores and remove special characters
  return filename.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '')
}

const EMPTY_FIELD = {
  label: '',
  type: 'text',
  placeholder: '',
  description: '',
  options: [],
  required: false,
  multiple: false
}

const AdditionalFields = ({
  entity,
  updateHandler,
  backHandler,
  create,
  update,
  isLoading
}) => {
  const { t } = useTranslation()

  const initial = useRef(
    JSON.parse(entity.app_settings?.application_input_additional_fields || '[]')
  )

  const [edit, setEdit] = useState(null)
  const [fields, setFields] = useState(initial.current)
  const [savedFields, setSavedFields] = useState({})
  const [newField, setNewField] = useState(null)

  const handleDropdownEvent = ({ id }) => {
    if (edit && edit === id) setEdit(null)
    else setEdit(id)
  }

  const createField = () => {
    const field = {
      id: uuidv4(),
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      ...EMPTY_FIELD
    }

    setEdit(field.id)
    setNewField(field.id)

    // Make sure we're adding a new field with a unique ID
    setFields((previous) => {
      const newFields = [...previous, field]
      return newFields
    })
  }

  const updateField = ({ field }) => {
    // Update the field in the fields array
    setFields((previous) =>
      previous.map((item) => (item.id === field.id ? field : item))
    )
  }

  const deleteField = ({ id }) => {
    setFields((previous) => previous.filter((item) => item.id !== id))
  }

  const addOption = ({ field }) => {
    const option = {
      id: uuidv4(),
      value: ''
    }

    updateField({
      field: {
        ...field,
        options: [...(field.options || []), option]
      }
    })
  }

  const updateOption = ({ field, optionId, value }) => {
    updateField({
      field: {
        ...field,
        options: field.options.map((option) =>
          option.id === optionId ? { ...option, value } : option
        )
      }
    })
  }

  const removeOption = ({ field, optionId }) => {
    updateField({
      field: {
        ...field,
        options: field.options.filter((option) => option.id !== optionId)
      }
    })
  }

  const handleSubmit = () => {
    // Ensure all file fields have sanitized labels before submitting
    const sanitizedFields = fields.map((field) => {
      if (field.type === 'file' && field.label) {
        return {
          ...field,
          label: sanitizeFileName(field.label),
          // Stelle sicher, dass placeholder und description synchronisiert sind
          placeholder: field.description || field.placeholder,
          description: field.description || field.placeholder
        }
      }
      return field
    })

    updateHandler({
      application_input_additional_fields: sanitizedFields
    })
  }

  return (
    <div>
      <ul className='space-y-4'>
        {!fields.length && (
          <li className='rounded-md bg-gray-50 px-4 py-4 text-center text-sm text-gray-500'>
            {t('career.flex.no_fields')}
            <div className='mt-2'>
              <Button.SecondaryBase
                appearance='plus'
                text={t('career.flex.create_field_action')}
                onClick={createField}
              />
            </div>
          </li>
        )}
        {fields.map((item) => (
          <li
            key={item.id}
            className={`overflow-hidden rounded-md bg-gray-50 shadow ${
              newField === item.id ? 'border-2 border-blue-500' : ''
            }`}
          >
            <div className='flex items-center justify-between px-4 py-5 sm:px-6'>
              <div>
                <h3 className='text-base font-semibold leading-6 text-gray-900'>
                  {item.label || t('career.flex.new_field')}
                </h3>
                <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                  {t(`career.flex.${item.type}_type`)}
                </p>
              </div>
              <div className='flex items-center space-x-2'>
                <button
                  type='button'
                  className='inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  onClick={() => handleDropdownEvent({ id: item.id })}
                >
                  {edit === item.id
                    ? t('career.flex.close_action')
                    : t('career.flex.edit_action')}
                </button>
                <button
                  type='button'
                  className='inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  onClick={() => deleteField({ id: item.id })}
                >
                  {t('career.flex.delete_action')}
                </button>
              </div>
            </div>
            <div
              className={`border-t border-gray-200 ${
                edit !== item.id && 'hidden'
              }`}
            >
              <Formik
                initialValues={{
                  label: item.label,
                  type: item.type,
                  placeholder: item.placeholder,
                  description: item.description || item.placeholder, // Unterstützung für bestehende Daten
                  required: item.required,
                  multiple: item.multiple || false
                }}
                onSubmit={(values) => {
                  // Ensure label is sanitized for file type
                  if (values.type === 'file' && values.label) {
                    values.label = values.label
                      .replace(/\s+/g, '_')
                      .replace(/[^a-zA-Z0-9_-]/g, '')

                    // Für Datei-Upload-Felder, setze placeholder auf description für Abwärtskompatibilität
                    values.placeholder = values.description
                  }
                  // For other field types, keep the label as is (no space replacement)

                  updateField({
                    field: {
                      ...item,
                      ...values
                    }
                  })
                  // Mark this field as saved
                  setSavedFields((prev) => ({
                    ...prev,
                    [item.id]: true
                  }))
                  // If this was a new field, it's no longer new after saving
                  if (newField === item.id) {
                    setNewField(null)
                  }
                }}
              >
                {(formik) => (
                  <form
                    className='flex max-w-lg flex-col gap-y-4 p-4'
                    onSubmit={formik.handleSubmit}
                  >
                    <div className='mb-2'>
                      <label className='mb-1 block text-sm font-medium text-gray-700'>
                        {t('career.flex.field_type_label')}
                      </label>
                      <div className='flex space-x-4'>
                        <label className='inline-flex items-center'>
                          <input
                            type='radio'
                            className='form-radio h-4 w-4 text-blue-600'
                            name='type'
                            value='text'
                            checked={formik.values.type === 'text'}
                            onChange={() => {
                              formik.setFieldValue('type', 'text')
                              updateField({
                                field: {
                                  ...item,
                                  type: 'text',
                                  options: []
                                }
                              })
                            }}
                          />
                          <span className='ml-2'>
                            {t('career.flex.text_input')}
                          </span>
                        </label>
                        <label className='inline-flex items-center'>
                          <input
                            type='radio'
                            className='form-radio h-4 w-4 text-blue-600'
                            name='type'
                            value='dropdown'
                            checked={formik.values.type === 'dropdown'}
                            onChange={() => {
                              formik.setFieldValue('type', 'dropdown')
                              updateField({
                                field: {
                                  ...item,
                                  type: 'dropdown',
                                  placeholder: '',
                                  options:
                                    item.options && item.options.length > 0
                                      ? item.options
                                      : [{ id: uuidv4(), value: '' }],
                                  multiple: item.multiple || false
                                }
                              })
                            }}
                          />
                          <span className='ml-2'>
                            {t('career.flex.dropdown')}
                          </span>
                        </label>
                        <label className='inline-flex items-center'>
                          <input
                            type='radio'
                            className='form-radio h-4 w-4 text-blue-600'
                            name='type'
                            value='file'
                            checked={formik.values.type === 'file'}
                            onChange={() => {
                              formik.setFieldValue('type', 'file')
                              updateField({
                                field: {
                                  ...item,
                                  type: 'file',
                                  options: []
                                }
                              })
                            }}
                          />
                          <span className='ml-2'>
                            {t('career.flex.field_type_file')}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <InputFormik
                        id='label'
                        label={t('career.flex.label_label')}
                        placeholder={t('career.flex.label_placeholder')}
                        description={t('career.flex.label_description')}
                        formik={formik}
                        onChange={(e) => {
                          // For all field types, replace spaces with underscores
                          const value = e.target.value.replace(/\s+/g, '_')

                          // For file type, also remove special characters
                          if (formik.values.type === 'file') {
                            const sanitizedValue = value.replace(
                              /[^a-zA-Z0-9_-]/g,
                              ''
                            )
                            formik.setFieldValue('label', sanitizedValue)
                          } else {
                            // For other field types, just replace spaces
                            formik.setFieldValue('label', value)
                          }
                        }}
                      />
                      {formik.values.type === 'file' && (
                        <div className='mt-1 text-sm text-gray-500'>
                          {t('career.flex.file_name_example', {
                            filename: `${sanitizeFileName(formik.values.label) || sanitizeFileName(t('career.flex.label_placeholder'))}.pdf`
                          })}
                        </div>
                      )}
                    </div>

                    {formik.values.type === 'dropdown' && (
                      <>
                        <div className='mb-4'>
                          <label className='block text-sm font-medium text-gray-700'>
                            {t('career.flex.dropdown_type_label')}
                          </label>
                          <div className='mt-2 flex space-x-4'>
                            <label className='inline-flex items-center'>
                              <input
                                type='radio'
                                className='form-radio h-4 w-4 text-blue-600'
                                name='multiple'
                                checked={!formik.values.multiple}
                                onChange={() => {
                                  formik.setFieldValue('multiple', false)
                                  updateField({
                                    field: {
                                      ...item,
                                      multiple: false
                                    }
                                  })
                                }}
                              />
                              <span className='ml-2'>
                                {t('career.flex.single_choice')}
                              </span>
                            </label>
                            <label className='inline-flex items-center'>
                              <input
                                type='radio'
                                className='form-radio h-4 w-4 text-blue-600'
                                name='multiple'
                                checked={formik.values.multiple}
                                onChange={() => {
                                  formik.setFieldValue('multiple', true)
                                  updateField({
                                    field: {
                                      ...item,
                                      multiple: true
                                    }
                                  })
                                }}
                              />
                              <span className='ml-2'>
                                {t('career.flex.multi_choice')}
                              </span>
                            </label>
                          </div>
                        </div>

                        <div className='my-6 border-t border-gray-200 pt-6'>
                          <div className='mb-2 flex items-center justify-between'>
                            <label className='block text-sm font-medium text-gray-700'>
                              {t('career.flex.dropdown_options')}
                            </label>
                            <Button.SecondaryBase
                              appearance='plus'
                              text={t('career.flex.add_option')}
                              onClick={() => addOption({ field: item })}
                            />
                          </div>
                          <div className='mb-6 space-y-2'>
                            {item.options &&
                              item.options.map((option) => (
                                <div
                                  key={option.id}
                                  className='flex items-center gap-x-2'
                                >
                                  <input
                                    type='text'
                                    className='block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm'
                                    value={option.value}
                                    onChange={(e) =>
                                      updateOption({
                                        field: item,
                                        optionId: option.id,
                                        value: e.target.value
                                      })
                                    }
                                    placeholder={t(
                                      'career.flex.option_placeholder'
                                    )}
                                  />
                                  <button
                                    type='button'
                                    className='inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                    onClick={() =>
                                      removeOption({
                                        field: item,
                                        optionId: option.id
                                      })
                                    }
                                  >
                                    {t('career.flex.delete_option')}
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    {formik.values.type === 'text' && (
                      <div>
                        <InputFormik
                          id='placeholder'
                          label={t('career.flex.placeholder_label')}
                          placeholder={t('career.flex.placeholder_placeholder')}
                          description={t('career.flex.placeholder_description')}
                          formik={formik}
                        />
                        <div className='mt-6 border-t border-gray-200 pt-4'>
                          <Toggle
                            appearance='with-left-label-and-description'
                            label={t('career.flex.required_label')}
                            description={t('career.flex.required_description')}
                            enabled={formik.values.required}
                            setEnabled={() => {
                              formik.setFieldValue(
                                'required',
                                !formik.values.required
                              )
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {formik.values.type === 'file' && (
                      <div>
                        <InputFormik
                          id='description'
                          label={t('career.flex.file_description_label')}
                          placeholder={t(
                            'career.flex.file_description_placeholder'
                          )}
                          description={t('career.flex.file_description_help')}
                          formik={formik}
                        />
                        <div className='mt-6 border-t border-gray-200 pt-4'>
                          <Toggle
                            appearance='with-left-label-and-description'
                            label={t('career.flex.required_label')}
                            description={t('career.flex.required_description')}
                            enabled={formik.values.required}
                            setEnabled={() => {
                              formik.setFieldValue(
                                'required',
                                !formik.values.required
                              )
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {formik.values.type === 'dropdown' && (
                      <div className='mt-6 border-t border-gray-200 pt-4'>
                        <Toggle
                          appearance='with-left-label-and-description'
                          label={t('career.flex.required_label')}
                          description={t('career.flex.required_description')}
                          enabled={formik.values.required}
                          setEnabled={() => {
                            formik.setFieldValue(
                              'required',
                              !formik.values.required
                            )
                          }}
                        />
                      </div>
                    )}

                    {/* Add a Save button to the form */}
                    <div className='mt-6 flex justify-end border-t border-gray-200 pt-6'>
                      {savedFields[item.id] ? (
                        <div className='flex items-center space-x-2'>
                          <span className='text-sm text-green-600'>
                            ✓ {t('career.flex.field_saved')}
                          </span>
                          <button
                            type='submit'
                            className='inline-flex items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                            onClick={() => {
                              // Reset saved state after 1.5 seconds to allow for changes
                              setTimeout(() => {
                                setSavedFields((prev) => ({
                                  ...prev,
                                  [item.id]: false
                                }))
                              }, 1500)
                            }}
                          >
                            {t('career.flex.save_field')}
                          </button>
                        </div>
                      ) : (
                        <button
                          type='submit'
                          className='inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        >
                          {t('career.flex.save_field')}
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </li>
        ))}
      </ul>

      {!!fields.length && (
        <div className='mt-6 bg-gray-50 px-4 py-4 text-right sm:rounded-md sm:px-6'>
          <Button.SecondaryBase
            appearance='plus'
            text={t('career.flex.create_field_action')}
            onClick={createField}
          />
        </div>
      )}

      <div className='mt-10 border-t border-gray-200 pt-6'>
        <div className='flex flex-col items-end'>
          <div className='mb-3 rounded-md bg-blue-50 p-3 text-sm text-blue-800'>
            <p className='flex items-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='mr-2 h-5 w-5'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                  clipRule='evenodd'
                />
              </svg>
              {t('career.flex.save_reminder')}
            </p>
          </div>

          <div className='flex justify-end gap-x-2'>
            {create && (
              <Button.PrimaryLG
                text={t('career.flex.create_flex.next_action')}
                onClick={handleSubmit}
                disabled={fields.some(({ label }) => !label)}
              />
            )}
            {update && (
              <>
                <Button.SecondaryLG
                  text={t('career.flex.create_flex.back_action')}
                  onClick={backHandler}
                />
                <Button.PrimaryLG
                  text={t('career.flex.create_flex.submit_action')}
                  onClick={handleSubmit}
                  disabled={
                    fields.some(({ label }) => !label) ||
                    (fields.length === initial.current.length &&
                      isEqual(initial.current, fields))
                  }
                  isLoading={isLoading}
                />
                {fields.some(({ label }) => !label) && (
                  <div className='mt-2 text-sm text-red-500'>
                    {t('career.flex.error_empty_labels')}
                  </div>
                )}
                {!fields.some(({ label }) => !label) &&
                  fields.length === initial.current.length &&
                  isEqual(initial.current, fields) && (
                    <div className='mt-2 text-sm text-red-500'>
                      {t('career.flex.error_no_changes')}
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

AdditionalFields.propTypes = {
  entity: PropTypes.object.isRequired,
  updateHandler: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  create: PropTypes.bool.isRequired,
  update: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool
}

AdditionalFields.defaultProps = {
  isLoading: false
}

export default AdditionalFields
